import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoginPageLayout from "../../components/LoginPageLayout";
import AuthForm from "../../components/AuthForm";
import stylesForms from "../../components/styles/auth-forms.module.css";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "notistack";
import { emailRegex, passwordRegex } from "../../utils/constants";
import { triggerGa } from "../../utils/gaUtils";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { sendOTPMutation, resetPasswordMutation } = useAuth();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState("email");
  const [error, setError] = useState(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0); // Add countdown state

  useEffect(() => {
    if (secondsLeft > 0) {
      const timerId = setInterval(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timerId);
    } else {
      setResendDisabled(false);
    }
  }, [secondsLeft]);

  const handleSendOtp = async (e) => {
    triggerGa({
      category: "Login",
      action: "button_click",
      label: "send_otp",
      label2: "",
      page: "login",
      screenName: "forgot_password",
    });
    e.preventDefault();
    setError(null);

    if (!email) {
      enqueueSnackbar("Email is required", { variant: "default" });
      setError("Email is required");
      return;
    }
    if (!emailRegex.test(email)) {
      enqueueSnackbar("Please enter a valid email address", {
        variant: "error",
      });
      setError("Please enter a valid email address");
      return;
    }

    sendOTPMutation.mutate(
      { email, purpose: "PASSWORD_RESET" },
      {
        onSuccess: () => {
          enqueueSnackbar("OTP sent to your mail", { variant: "success" });
          if (step === "email") setStep("reset");
          setError(null);
          setResendDisabled(true);
          setSecondsLeft(30);
        },
        onError: (error) => {
          console.error("OTP send failed:", error);
          if (error.response?.data?.message === "Email not registered!") {
            triggerGa({
              category: "Error",
              action: "email_not_registered",
              label: "",
              label2: "",
              page: "login",
              screenName: "forgot_password",
            });
          }
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to send OTP",
            { variant: "error" },
          );
          setResendDisabled(false);
          setSecondsLeft(0);
        },
      },
    );
  };

  const handleResetPassword = async (e) => {
    triggerGa({
      category: "Login",
      action: "button_click",
      label: "reset_password",
      label2: "",
      page: "login",
      screenName: "forgot_password",
    });
    e.preventDefault();
    setError(null);

    if (!passwordRegex.test(newPassword)) {
      const errorMessage =
        "Password must contain: 8+ characters, 1 uppercase, 1 lowercase, 1 number, 1 special character";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setError(errorMessage);
      return;
    }

    if (newPassword !== confirmPassword) {
      triggerGa({
        category: "Error",
        action: "passwords_don't_match",
        label: "",
        label2: "",
        page: "login",
        screenName: "forgot_password",
      });
      enqueueSnackbar("Passwords do not match", { variant: "error" });
      setError("Passwords do not match");
      return;
    }

    resetPasswordMutation.mutate(
      { email, otp, newPassword },
      {
        onSuccess: () => {
          navigate("/login", {
            state: { successMessage: "Password reset successful!" },
          });
        },
        onError: (error) => {
          if (error?.response?.data?.message === "Invalid OTP.") {
            triggerGa({
              category: "Error",
              action: "invalid OTP",
              label: "",
              label2: "",
              page: "login",
              screenName: "forgot_password",
            });
          }
          setError(
            error?.response?.data?.message ||
              "Failed to reset password. Please try again.",
          );
          enqueueSnackbar(
            error?.response?.data?.message ||
              "Failed to reset password. Please try again.",
            { variant: "error" },
          );
        },
      },
    );
  };

  const isResendDisabled = resendDisabled || sendOTPMutation.isPending;

  return (
    <LoginPageLayout>
      <AuthForm
        title="Forgot your Password?"
        onSubmit={step === "email" ? handleSendOtp : handleResetPassword}
        buttonText={step === "email" ? "Send OTP" : "Reset Password"}
        loading={
          step === "email"
            ? sendOTPMutation.isPending
            : resetPasswordMutation.isPending
        }
        additionalLinks={
          step === "email" ? (
            <Link to="/login" className={stylesForms.blueLink}>
              Take me back to Sign-in page
            </Link>
          ) : (
            <div className={stylesForms.linkContainer}>
              <span
                onClick={(e) => !isResendDisabled && handleSendOtp(e)}
                className={`${stylesForms.redLink} ${isResendDisabled ? stylesForms.disabledLink : ""}`}
              >
                {sendOTPMutation.isPending
                  ? "Sending OTP..."
                  : isResendDisabled
                    ? `Resend OTP available in ${secondsLeft}s`
                    : "Didn't Receive OTP?"}
              </span>
              <Link to="/login" className={stylesForms.blueLink}>
                Back to Login
              </Link>
            </div>
          )
        }
      >
        {step === "email" ? (
          <>
            <div className={stylesForms.subHeading}>
              Don't Worry! Enter your registered email address below and we'll
              send you an OTP to reset your password :)
            </div>
            <div className={stylesForms.formGroup}>
              <label htmlFor="email" className={stylesForms.label}>
                Enter Registered Email
              </label>
              <input
                type="email"
                id="email"
                autoComplete="username"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                className={stylesForms.input}
                required
                aria-label="Email address"
                aria-required="true"
                aria-invalid={!!error && !email}
              />
            </div>
          </>
        ) : (
          <>
            <div className={stylesForms.formGroup}>
              <label htmlFor="otp" className={stylesForms.label}>
                Enter OTP sent to email
              </label>
              <input
                type="text"
                id="otp"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value);
                  setError(null);
                }}
                className={stylesForms.input}
                required
                maxLength={6}
                aria-label="One-time password"
                aria-required="true"
              />
            </div>
            <div className={stylesForms.formGroup}>
              <label htmlFor="newPassword" className={stylesForms.label}>
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError(null);
                }}
                className={stylesForms.input}
                required
                aria-label="New password"
                aria-required="true"
              />
            </div>
            <div className={stylesForms.formGroup}>
              <label htmlFor="confirmPassword" className={stylesForms.label}>
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError(null);
                }}
                className={stylesForms.input}
                required
                aria-label="Confirm new password"
                aria-required="true"
              />
            </div>
          </>
        )}
      </AuthForm>
    </LoginPageLayout>
  );
}
